/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import type { TagMultiSelectOption } from './tag-multi-select-form-field'

export type {
  TagMultiSelectFormFieldProps,
  TagMultiSelectOnValueChangeTrigger,
  TagMultiSelectOption,
} from './tag-multi-select-form-field'
export { TagMultiSelectFormField } from './tag-multi-select-form-field'

/** Parse comma-separated string to string[] for form default value */
export function tagMultiSelectFromString(value: string | null | undefined): string[] {
  if (!value || value.trim() === '') return []
  return value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
}

/** Format string[] to comma-separated string for URL/API */
export function tagMultiSelectToString(values: string[]): string {
  return values.filter(Boolean).join(',')
}

/** Build options from enum (keys only, skips numeric keys) */
export function tagMultiSelectFromEnum<T extends Record<string, string | number>>(
  enumValue: T,
  labelFn: (key: string) => string = (k) => k
): TagMultiSelectOption[] {
  const result: { label: string; value: string }[] = []
  for (const entry of Object.keys(enumValue)) {
    if (Number.isNaN(Number(entry))) {
      result.push({ label: labelFn(entry), value: entry })
    }
  }
  return result
}

/** Build options from tag-like objects with name property (label and value both use name). */
export function tagMultiSelectFromTags<T extends { name: string }>(tags: T[]): TagMultiSelectOption[] {
  return (tags ?? []).map((t) => ({ label: t.name, value: t.name }))
}
